import React, { useEffect, useState, useCallback } from "react";
import { Activity, Check } from "lucide-react";
import { toast } from "sonner";
import {
  getAthletePendingStravaMatches,
  resolveAthleteStravaMatch,
} from "../../lib/api";

function fmtActivityDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Athlete-side banner that lists Strava activities the auto-matcher
 * couldn't confidently assign (double-day cases — two prescribed workouts
 * on the same date). Each one expands into a one-tap chooser so the
 * athlete picks the right workout (or "Not on the plan today" to leave
 * it as an extra activity).
 *
 * Designed to disappear once everything is resolved, so on a normal week
 * the athlete sees nothing here.
 */
export default function StravaMatchChooser({ onResolved }) {
  const [pending, setPending] = useState(null);
  const [working, setWorking] = useState(null); // strava_activity_id mid-flight

  const reload = useCallback(() => {
    getAthletePendingStravaMatches()
      .then(setPending)
      .catch(() => setPending([]));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const resolve = async (activity, choice) => {
    setWorking(activity.strava_activity_id);
    try {
      await resolveAthleteStravaMatch(activity.strava_activity_id, choice);
      toast.success(choice?.workout_id ? "Matched" : "Marked as extra");
      reload();
      if (typeof onResolved === "function") onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setWorking(null);
    }
  };

  if (!pending || pending.length === 0) return null;

  return (
    <div
      className="border border-[#fc4c02] bg-[#fc4c02]/5 px-4 py-4 mb-6"
      data-testid="strava-match-chooser"
    >
      <div className="flex items-center gap-2 mb-3">
        <Activity size={16} className="text-[#fc4c02]" />
        <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#fc4c02]">
          Match Strava activit{pending.length === 1 ? "y" : "ies"} to your plan
        </div>
      </div>
      <p className="text-[12px] text-[var(--tir-muted)] mb-4 leading-relaxed">
        We saw more than one workout on the same day for {pending.length === 1 ? "this run" : "these runs"}.
        Pick which prescribed workout each matches — or skip if it wasn't on the plan.
      </p>
      <ul className="space-y-3">
        {pending.map((a) => {
          const km = (a.distance_m || 0) / 1000;
          const mins = Math.round((a.moving_time_s || 0) / 60);
          return (
            <li
              key={a.strava_activity_id}
              className="bg-white border border-[var(--tir-border)] p-3"
              data-testid={`strava-pending-${a.strava_activity_id}`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[var(--tir-ink)] truncate">
                    {a.name || "Strava activity"}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mt-0.5">
                    {a.sport_type || "Run"} · {fmtActivityDate(a.start_date_local)}
                  </div>
                </div>
                <div className="font-mono-tir text-xs text-[var(--tir-ink)] whitespace-nowrap shrink-0">
                  {km > 0 ? `${km.toFixed(1)} km` : ""}
                  {km > 0 && mins > 0 ? " · " : ""}
                  {mins > 0 ? `${mins} min` : ""}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {(a.match_candidates || []).map((c) => (
                  <button
                    key={`${c.program_id}-${c.workout_id}`}
                    type="button"
                    onClick={() =>
                      resolve(a, {
                        program_id: c.program_id,
                        week_number: c.week_number,
                        day_label: c.day_label,
                        workout_id: c.workout_id,
                      })
                    }
                    disabled={working === a.strava_activity_id}
                    data-testid={`strava-match-option-${a.strava_activity_id}-${c.workout_id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 border border-[var(--tir-border)] hover:border-[#fc4c02] hover:bg-[#fc4c02]/5 transition-colors text-left disabled:opacity-50"
                  >
                    <span className="text-sm text-[var(--tir-ink)]">
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mr-2">
                        {c.day_label}
                      </span>
                      {c.title || c.type || "Workout"}
                      {c.duration_or_distance && (
                        <span className="text-[var(--tir-muted)] ml-1">
                          · {c.duration_or_distance}
                        </span>
                      )}
                    </span>
                    <Check size={14} className="text-[#fc4c02] opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => resolve(a, null)}
                  disabled={working === a.strava_activity_id}
                  data-testid={`strava-match-skip-${a.strava_activity_id}`}
                  className="px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] self-start mt-1"
                >
                  Not on the plan today
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
